'use client'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'

import * as MapModel from '@/components/map/components/model'
import type { GenerateSvgStringParams } from '@/components/map/components/naverMap/view'

import exploreEvents from '@/utils/gaEvents/exploreEvents'
import fabEvent from '@/utils/gaEvents/fabEvent'

import useHash from '@/shared/hooks/useHash'
import useHashChange from '@/shared/hooks/useHashChange'
import { useInitNaverSdk } from '@/shared/hooks/useInitNaverSdk'
import type { Dispatch, SetStateAction } from 'react'

import { SALE_TYPE, PARKINGLOT_TYPE, SHEET_STATE_TYPE } from '@/enums/detail'
import { CATE_CATEGORY } from '@/enums/gtagEvents'
import { HASH_KEY_TYPE } from '@/enums/map'

import { ParkinglotDetail, SharedParkinglotDetail } from '@/api/server/detail/dto/detailDto'
import { MapToastMessage, AirportMarkers } from '@/constants/messages/map'

type Map = naver.maps.Map
type Marker = naver.maps.Marker

export interface MapViewModel {
  texts: object
  states: {
    naverMapRef: React.MutableRefObject<Map | null>
    selectedMarkerRef: React.MutableRefObject<Marker | null>
    cachedMarker: React.MutableRefObject<{ [key: string]: Marker }>
    zoomToast: string
    markerToast: string
    isCurrentLocation: boolean
  }
  setters: {
    setIsCurrentLocation: Dispatch<SetStateAction<boolean>>
  }
  handlers: {
    onFilterButtonClick: () => void
    handleSelectedMarkerOverlay: () => void
  }
}

export interface useNaverMapViewModelProps {
  lat: number
  lng: number
  pin?: ParkinglotDetail | SharedParkinglotDetail
  setLocation: Dispatch<
    SetStateAction<{
      default: { lat: number; lng: number }
    }>
  >
  pins: { [key: string]: MapModel.Pin[] }
  generateSvgString: (params: GenerateSvgStringParams) => string
  searchLat?: string
  searchLng?: string
  id?: string
  type?: PARKINGLOT_TYPE
  searchedLocationMarker?: string
  airportMarker: string
  isFilterOn: boolean
  zoomLevel: number
  setIsFilterOn: Dispatch<SetStateAction<boolean>>
  setZoomLevel: Dispatch<SetStateAction<number>>
}

const useNaverMapViewModel = ({
  lat,
  lng,
  setLocation,
  pins,
  generateSvgString,
  searchLat,
  searchLng,
  searchedLocationMarker,
  airportMarker,
  type,
  id,
  isFilterOn,
  setIsFilterOn,
  zoomLevel,
  setZoomLevel
}: useNaverMapViewModelProps): MapViewModel => {
  const router = useRouter()
  const { getHashByKey, updateHash } = useHash()
  const onChangeHash = useHashChange(HASH_KEY_TYPE.SHEET)
  const cachedMarker = useRef<{ [key: string]: Marker }>({})
  const selectedMarkerRef = useRef<Marker | null>(null)
  const naverMapRef = useRef<Map | null>(null)
  const searchedMarkerRef = useRef<Marker | null>(null)
  const [isMapReady, setIsMapReady] = useState(false)
  const [zoomToast, setZoomToast] = useState('')
  const [markerToast, setMarkerToast] = useState('')
  const [isCurrentLocation, setIsCurrentLocation] = useState(false)
  const { isLoadScript } = useInitNaverSdk()

  const getNaverMap = () => {
    const container = document.getElementById('map') as HTMLDivElement
    const center = new naver.maps.LatLng(lat, lng)
    const map: Map = new naver.maps.Map(container, {
      center,
      zoom: zoomLevel,
      minZoom: 0,
      maxZoom: 22,
      logoControlOptions: {
        position: naver.maps.Position.BOTTOM_LEFT
      },
      mapDataControl: false,
      scaleControl: false
    })
    return map
  }

  // emit location
  const catchDragEvent = () => {
    const listener = naver.maps.Event.addListener(naverMapRef.current!, 'idle', () => {
      const { x, y } = naverMapRef.current!.getCenter()
      setLocation((prev) => ({ ...prev, default: { lat: y, lng: x } }))
    })

    return listener
  }

  const catchDragStartEvent = () => {
    const listener = naver.maps.Event.addListener(naverMapRef.current!, 'dragstart', () => {
      setIsCurrentLocation(false)
    })

    return listener
  }

  // emit location
  const catchZoomEvent = () => {
    const listener = naver.maps.Event.addListener(naverMapRef.current!, 'zoom_changed', (level) => {
      setZoomLevel(level)
    })
    return listener
  }

  const mapClickEvent = () => {
    const listener = naver.maps.Event.addListener(naverMapRef.current!, 'click', () => {
      if (getHashByKey(HASH_KEY_TYPE.SHEET) !== SHEET_STATE_TYPE.CLOSE) {
        const pathname = window.location.pathname
        const searchParams = window.location.search
        const hash = updateHash({
          sheet: SHEET_STATE_TYPE.CLOSE
        })
        router.push(`${pathname}${searchParams}${hash}`)
      }
      if (selectedMarkerRef.current) {
        handleSelectedMarker(selectedMarkerRef.current, false)
      }
    })
    return listener
  }

  // NOTE: 마커의 content(htmlString)의 크기를 동적으로 계산하기 위한 함수
  const getDynamicMarkerSize = (htmlString: string) => {
    const temporaryDiv = document.createElement('div')
    temporaryDiv.style.position = 'absolute'
    temporaryDiv.style.left = '-9999px'
    temporaryDiv.innerHTML = htmlString
    document.body.appendChild(temporaryDiv)
    const size = {
      width: temporaryDiv.offsetWidth,
      height: temporaryDiv.offsetHeight
    }
    document.body.removeChild(temporaryDiv)
    return { ...size }
  }

  const generateMarker = (
    pin: MapModel.Pin,
    info: {
      isOn: boolean
      geohash: string
    }
  ): Marker => {
    const { isOn, geohash } = info
    const latitude = pin.lat
    const longitude = pin.lng
    const zIndex = isOn ? 100 : pin.getOrderedZIndex(pin.MarkerType)
    const content = generateSvgString({
      name: pin.getMarkerName(),
      price: pin.getMarkerPrice(),
      markerType: pin.MarkerType,
      isOn: isOn
    })
    const { width, height } = getDynamicMarkerSize(content)
    const icon = {
      content,
      size: new naver.maps.Size(width, height),
      anchor: new naver.maps.Point(width / 2, height)
    }

    const marker = new window.naver.maps.Marker({
      position: new naver.maps.LatLng(latitude, longitude),
      map: naverMapRef.current!,
      icon,
      zIndex
    })

    // TODO: keys to enum
    marker.set('seq', pin.seq)
    marker.set('type', pin.type)
    marker.set('name', pin.getMarkerName())
    marker.set('price', pin.getMarkerPrice())
    marker.set('markerType', pin.MarkerType)
    marker.set('isPartner', pin.isPartner ?? null)
    marker.set('getOrderedZIndex', pin.getOrderedZIndex)
    marker.set('zIndex', zIndex)
    marker.set('isOn', isOn)
    marker.set('geohash', geohash)

    return marker
  }

  const generateSearchMarker = ({ searchedLat, searchedLng }: { searchedLat: string; searchedLng: string }) => {
    const searchedCenter = new naver.maps.LatLng(Number(searchedLat), Number(searchedLng))

    if (naverMapRef.current == null) return

    if (searchedMarkerRef.current) {
      searchedMarkerRef.current.setMap(null)
    }

    const searchedMarker = new naver.maps.Marker({
      position: searchedCenter,
      map: naverMapRef.current!,
      icon: {
        content: searchedLocationMarker || ''
      },
      zIndex: 99
    })

    searchedMarkerRef.current = searchedMarker
  }

  const handleSelectedMarker = (marker: Marker, isOn: boolean) => {
    const content = generateSvgString({
      name: marker.get('name'),
      price: marker.get('price'),
      markerType: marker.get('markerType'),
      isOn: isOn
    })
    const { width, height } = getDynamicMarkerSize(content)
    marker.setIcon({
      content,
      size: new naver.maps.Size(width, height),
      anchor: new naver.maps.Point(width / 2, height)
    })
    if (isOn) {
      marker.setZIndex(100)
    } else {
      const getOrderedZIndex = marker.get('getOrderedZIndex')
      const markerType = marker.get('markerType')
      const zIndex = getOrderedZIndex(markerType)
      marker.setZIndex(zIndex)
    }
  }

  const drawAirportMarkers = () => {
    if (!naverMapRef.current) return

    AirportMarkers.forEach(({ key, position }) => {
      const marker = new naver.maps.Marker({
        position: new naver.maps.LatLng(position.lat, position.lng),
        map: naverMapRef.current!,
        icon: {
          content: airportMarker
        },
        zIndex: 1
      })
      cachedMarker.current[key] = marker
    })
  }

  const handleEmptyMarkersInView = () => {
    if (naverMapRef.current == null) return
    const mapBounds = naverMapRef.current?.getBounds() as naver.maps.LatLngBounds
    const isInMap = Object.keys(cachedMarker.current).some((key) => {
      return (
        mapBounds.hasLatLng(cachedMarker.current[key].getPosition()) && !checkMarkerOverlay(cachedMarker.current[key])
      )
    })
    if (isInMap) {
      setMarkerToast(MapToastMessage.NONE)
    } else {
      setMarkerToast(MapToastMessage.NO_PARKING_INFO)
    }
  }

  const AIRPORT_ROUTE_MAP: Record<string, string> = {
    [AirportMarkers[0].key]: '/airport?iata=ICN&t=1',
    [AirportMarkers[1].key]: '/airport?iata=ICN&t=2',
    [AirportMarkers[2].key]: '/airport?iata=GMP&t=3'
  }

  const setEventForMarker = (marker: Marker, markerKey?: string) => {
    naver.maps.Event.addListener(marker, 'click', () => {
      if (markerKey && AIRPORT_ROUTE_MAP[markerKey]) {
        router.push(AIRPORT_ROUTE_MAP[markerKey])
        return
      }
      if (selectedMarkerRef.current) {
        handleSelectedMarker(selectedMarkerRef.current, false)
      }
      handleSelectedMarker(marker, true)
      selectedMarkerRef.current = marker
      const seq = marker.get('seq')
      const type = marker.get('type')
      const isPartner = marker.get('isPartner')
      const { x: lng, y: lat } = marker.getPosition()
      if (type === PARKINGLOT_TYPE.PARKINGLOT) {
        exploreEvents.partner.onLoadParkinglotSummary(
          isPartner ? CATE_CATEGORY.PARTNER : CATE_CATEGORY.NORMAL,
          seq,
          lat,
          lng
        )
      }
      if (type === PARKINGLOT_TYPE.SHARE) {
        exploreEvents.shared.onLoadParkinglotSummary(seq, lat, lng)
      }
      const hash = updateHash({ sheet: SHEET_STATE_TYPE.HIDDEN })
      router.push(`/map?type=${type}&id=${seq}${hash}`)
    })
  }

  const checkMarkerOverlay = (marker: Marker): boolean => {
    // NOTE: 지도의 줌 레벨이 15 이하일 때 오버레이 여부
    if (zoomLevel <= 15) {
      return true
    }

    // NOTE: 지도의 경계 안에서 오버레이 여부
    if (lat && lng) {
      const mapBounds = naverMapRef.current!.getBounds() as naver.maps.LatLngBounds
      if (!mapBounds.hasLatLng(marker.getPosition())) {
        return true
      }
    }

    // NOTE: 필터가 켜져있을 때 오버레이 여부
    if (isFilterOn) {
      if (!marker) return false
      const isPartnerType = marker.get('isPartner')
      const parkinglotType = marker.get('type')
      if (!isPartnerType && parkinglotType === PARKINGLOT_TYPE.PARKINGLOT) {
        return true
      }
    }

    return false
  }

  const handleMarkerOverlay = (marker: Marker) => {
    if (checkMarkerOverlay(marker)) {
      marker.setMap(null)
    } else {
      marker.setMap(naverMapRef.current!)
    }
  }

  const handleCachedMarkersOverlay = () => {
    const markerKeys = Object.keys(cachedMarker.current)
    markerKeys.forEach((markerKey) => {
      const marker = cachedMarker.current[markerKey]
      handleMarkerOverlay(marker)
    })
  }

  /**
   * id 변경 시 selectedMarkerRef, 지도 중심 변경
   * 시트가 닫혀있지 않을 때만 실행
   * selectedMarkerRef는 id가 변경될 때 항상 업데이트
   * 지도 중심 변경은 시트 완전 열려있을 때만 실행
   */
  const handleSelectedMarkerOverlay = () => {
    const isActiveSheet = getHashByKey(HASH_KEY_TYPE.SHEET) !== SHEET_STATE_TYPE.CLOSE
    const isFullSheet = getHashByKey(HASH_KEY_TYPE.SHEET) === SHEET_STATE_TYPE.VISIBLE

    if (!isActiveSheet) return

    if (selectedMarkerRef.current) {
      handleSelectedMarker(selectedMarkerRef.current, false)
    }

    Object.entries(cachedMarker.current).forEach(([_, marker]) => {
      if (marker.get('seq') === Number(id) && isActiveSheet) {
        selectedMarkerRef.current = marker
        handleSelectedMarker(marker, true)
      }
    })

    if (isFullSheet && selectedMarkerRef.current && naverMapRef.current) {
      naverMapRef.current.setCenter(selectedMarkerRef.current.getPosition())
    }
  }

  const onFilterButtonClick = () => {
    fabEvent.onClickFilterButton()
    setIsFilterOn((prev) => !prev)
    const isSaleBoolean = getHashByKey(HASH_KEY_TYPE.IS_SALE) === SALE_TYPE.ON
    const pathname = window.location.pathname
    const searchParams = window.location.search
    const hash = updateHash({
      [HASH_KEY_TYPE.IS_SALE]: isSaleBoolean ? SALE_TYPE.OFF : SALE_TYPE.ON
    })
    router.push(`${pathname}${searchParams}${hash}`)
  }

  useEffect(() => {
    handleSelectedMarkerOverlay()
  }, [id])

  useEffect(() => {
    const pinsKeys = Object.keys(pins) as (keyof MapModel.Pin)[] as string[]

    if (pinsKeys.length <= 0) return
    if (naverMapRef.current == null) return

    const isActiveSheet = getHashByKey(HASH_KEY_TYPE.SHEET) !== SHEET_STATE_TYPE.CLOSE
    pinsKeys.forEach((pinKey) => {
      pins[pinKey].forEach((pin) => {
        const geohash = pin.geohash.slice(0, 6)
        const key = `${geohash}/${pin.type}_${pin.seq}`
        const hasMarker = !!cachedMarker.current[key]
        const isOn = pin.type === type && pin.seq === Number(id)

        if (hasMarker) {
          cachedMarker.current[key].setMap(null)
          delete cachedMarker.current[key]
        }

        const marker = generateMarker(pin, { isOn, geohash })

        handleMarkerOverlay(marker)
        setEventForMarker(marker)
        cachedMarker.current[key] = marker

        if (isOn) {
          selectedMarkerRef.current = cachedMarker.current[key]
          handleSelectedMarker(selectedMarkerRef.current, isActiveSheet)
        }
      })
    })
    handleEmptyMarkersInView()
  }, [pins, isMapReady])

  useEffect(() => {
    if (naverMapRef.current == null) return
    const center = new naver.maps.LatLng(lat, lng)
    naverMapRef.current.setCenter(center)
    handleCachedMarkersOverlay()
    setTimeout(() => handleEmptyMarkersInView(), 800)
  }, [lat, lng])

  useEffect(() => {
    if (naverMapRef.current == null) return
    if (zoomLevel <= 15) {
      setZoomToast(MapToastMessage.ZOOM_TOO_WIDE)
    } else {
      setZoomToast('')
    }
    handleCachedMarkersOverlay()
    handleEmptyMarkersInView()
  }, [zoomLevel])

  useEffect(() => {
    if (naverMapRef.current == null) return
    handleCachedMarkersOverlay()
    handleEmptyMarkersInView()
  }, [isFilterOn])

  useEffect(() => {
    if (naverMapRef.current == null) return
    if (!searchLat || !searchLng) return

    generateSearchMarker({ searchedLat: searchLat, searchedLng: searchLng })
    handleCachedMarkersOverlay()
    handleEmptyMarkersInView()
  }, [naverMapRef.current, searchLat, searchLng])

  useEffect(() => {
    if (window.naver && isLoadScript) {
      naverMapRef.current = getNaverMap()
      setIsMapReady(true)

      drawAirportMarkers()
      setEventForMarker(cachedMarker.current['ICN_T1'], 'ICN_T1')
      setEventForMarker(cachedMarker.current['ICN_T2'], 'ICN_T2')
      setEventForMarker(cachedMarker.current['GMP'], 'GMP')

      const dragListener = catchDragEvent()
      const dragStartListener = catchDragStartEvent()
      const zoomListener = catchZoomEvent()
      const clickListener = mapClickEvent()

      return () => {
        naver.maps.Event.removeListener(dragListener)
        naver.maps.Event.removeListener(dragStartListener)
        naver.maps.Event.removeListener(zoomListener)
        naver.maps.Event.removeListener(clickListener)
      }
    }
  }, [isLoadScript])

  useEffect(() => {
    const hashChangeHandler = () => {
      const sheetState = getHashByKey(HASH_KEY_TYPE.SHEET) as SHEET_STATE_TYPE
      if (sheetState === SHEET_STATE_TYPE.CLOSE && selectedMarkerRef.current !== null) {
        handleSelectedMarker(selectedMarkerRef.current, false)
      }
      if (sheetState !== SHEET_STATE_TYPE.CLOSE && selectedMarkerRef.current !== null) {
        handleSelectedMarker(selectedMarkerRef.current, true)
      }
    }
    hashChangeHandler()
  }, [onChangeHash])

  return {
    texts: {},
    states: { naverMapRef, zoomToast, markerToast, isCurrentLocation, selectedMarkerRef, cachedMarker },
    setters: { setIsCurrentLocation },
    handlers: { onFilterButtonClick, handleSelectedMarkerOverlay }
  }
}

export default useNaverMapViewModel
